<template>
  <Modal
    no-padding
    hide-footer
    class="share-modal"
  >
    <div>
      <div class="flex items-center justify-between space-x-6 p-5 pb-0">
        <div>
          <InputLabel>{{ $t('share') }}:</InputLabel>
          <SelectInput
            v-model="activeShareOption"
            :options="shareOptions"
            name="share_option"
            class="mt-1.5"
            @update:model-value="(value) => loadOption(value)"
          >
            <template #label-selected>
              <div class="flex space-x-1.5">
                <ShareOptionIcon :type="activeShareOption" />
                <span>{{ label[activeShareOption] }}</span>
              </div>
            </template>

            <template #item-leading="{ item }">
              <ShareOptionIcon :type="item.value" />
            </template>
          </SelectInput>
        </div>
        <div>
          <div v-show="['facebook', 'instagram', 'linkedin', 'email'].includes(activeShareOption)">
            <InputLabel :ui="{ label: 'text-right' }">
              {{ $t('language') }}:
            </InputLabel>
            <SelectInput
              v-model="activeLanguage"
              :options="shareLanguages"
              name="share_language"
              position="right"
              class="mt-1.5"
            />
          </div>
        </div>
      </div>

      <div class="p-8">
        <ShareFacebookPanel
          v-if="isLoaded('facebook')"
          v-show="activeShareOption === 'facebook'"
          :ai-endpoint="endpoint"
          :ai-body="aiBody"
          :mode="facebookMode"
          :language="activeLanguage"
          :landing-page-url="landingPageUrl"
          :partner-name="partnerName"
          :initial-text="socialShareText"
          :media="media"
          :link-preview-image-url="fetchedLinkPreviewImageUrl"
        />

        <ShareInstagramPanel
          v-if="isLoaded('instagram')"
          v-show="activeShareOption === 'instagram'"
          :ai-endpoint="endpoint"
          :ai-body="aiBody"
          :language="activeLanguage"
          :landing-page-url="landingPageUrl"
          :partner-name="partnerName"
          :initial-text="socialShareText"
          :media="mediaEndpoint ? fetchedMedia : media"
          :is-media-loading="isMediaLoading"
        />

        <ShareAiDraftPanel
          v-if="isLoaded('linkedin')"
          v-show="activeShareOption === 'linkedin'"
          platform="linkedin"
          :ai-endpoint="endpoint"
          :ai-body="aiBody"
          :language="activeLanguage"
          :landing-page-url="landingPageUrl"
          :partner-name="partnerName"
          :initial-text="socialShareText"
          :copy-label="$t('copy_post')"
          open-href="https://linkedin.com"
          :open-label="$t('open_linkedin')"
        />

        <ShareWhatsappPanel
          v-if="isLoaded('whatsapp')"
          v-show="activeShareOption === 'whatsapp'"
          :landing-page-url="landingPageUrl"
        />

        <ShareAiDraftPanel
          v-if="isLoaded('email')"
          v-show="activeShareOption === 'email'"
          platform="email"
          :ai-endpoint="endpoint"
          :ai-body="aiBody"
          :language="activeLanguage"
          :landing-page-url="landingPageUrl"
          :copy-label="$t('copy_and_paste_in_email')"
        />

        <ShareQrCodePanel
          v-if="isLoaded('qrcode')"
          v-show="activeShareOption === 'qrcode'"
          :url="qrCodeUrl"
        />

        <ShareLinkPanel
          v-if="isLoaded('link')"
          v-show="activeShareOption === 'link'"
          :landing-page-url="landingPageUrl"
        />

        <ShareEmbedPanel
          v-if="isLoaded('embed') && isAdmin"
          v-show="activeShareOption === 'embed'"
          :embed-code="embedCode"
        />
      </div>
    </div>
  </Modal>
</template>

<script setup lang="ts">
import type { LandingPage } from '~/types/landing-page'
import type { ShareOption } from '~/types/partner'

export interface ShareModalProps {
  landingPageUrl: string
  partnerName: string
  embedCode: string
  socialShareText: string | null
  activeOption?: ShareOption
  media?: LandingPage['media'] | null
  aiEndpoint?: string
  aiBody?: Record<string, unknown>
  options?: ShareOption[]
  facebookMode?: 'link' | 'conversation'
  qrUrl?: string
  mediaEndpoint?: string
}

const props = withDefaults(defineProps<ShareModalProps>(), {
  activeOption: 'facebook',
  media: null,
  aiEndpoint: undefined,
  aiBody: undefined,
  options: undefined,
  facebookMode: undefined,
  qrUrl: undefined,
  mediaEndpoint: undefined
})

const { t, locale, locales } = useI18n()
// The experience version hashid only exists on content experience pages
const hashid = props.aiEndpoint ? '' : getVersionHashidFromUrl()
const endpoint = computed(() => props.aiEndpoint ?? `ai/extended-content-experiences/${hashid}`)

const { backendUrl } = useRuntimeConfig().public
const qrCodeUrl = computed(() => props.qrUrl ?? `${backendUrl}/shared-collection-personalisations/${hashid}/qr`)

// Share options
const label: Record<ShareOption, string> = {
  email: t('email'),
  facebook: 'Facebook',
  instagram: 'Instagram',
  link: t('link'),
  linkedin: 'LinkedIn',
  qrcode: t('qr_code'),
  whatsapp: 'WhatsApp',
  embed: t('embed_code')
}

const user = useUser()
const isAdmin = computed(() => user.value && [304, 566, 5814].includes(user.value.id))

const shareOptions = computed(() => {
  const options = [
    { label: 'Facebook', value: 'facebook' },
    { label: 'Instagram', value: 'instagram' },
    { label: 'LinkedIn', value: 'linkedin' },
    { label: 'WhatsApp', value: 'whatsapp' },
    { label: t('email'), value: 'email' },
    { label: t('qr_code'), value: 'qrcode' },
    { label: t('link'), value: 'link' }
  ]

  if (isAdmin.value) {
    options.push({ label: t('embed_code'), value: 'embed' })
  }

  return props.options ? options.filter(option => props.options?.includes(option.value as ShareOption)) : options
})

const shareLanguages = computed(() =>
  [...locales.value]
    .sort((a, b) => (a.name && b.name ? a.name.localeCompare(b.name) : 0))
    .map(i => ({ label: i.name ?? '', value: i.code }))
)

// Lazily mount each option's panel the first time it is selected, then keep it alive.
const loadedOptions = ref([props.activeOption])

const loadOption = (option: ShareOption) => {
  if (!loadedOptions.value.includes(option)) {
    loadedOptions.value.push(option)
  }
}

const isLoaded = (option: ShareOption) => loadedOptions.value.includes(option)

const activeShareOption = ref(props.activeOption)
const activeLanguage = ref(locale.value)

// Images that need fetching are only fetched once a panel that uses them is opened
const fetchedMedia = ref<LandingPage['media'] | null>(null)
const fetchedLinkPreviewImageUrl = ref<string | null>(null)
const isMediaLoading = ref(false)

const loadMedia = async (endpoint: string) => {
  isMediaLoading.value = true

  try {
    const response = await $larafetch<{ media: LandingPage['media'], link_preview_image_url: string | null }>(endpoint)
    fetchedMedia.value = response.media
    fetchedLinkPreviewImageUrl.value = response.link_preview_image_url
  } catch {
    fetchedMedia.value = null
    fetchedLinkPreviewImageUrl.value = null
  } finally {
    isMediaLoading.value = false
  }
}

const { mediaEndpoint } = props

if (mediaEndpoint) {
  until(() => isLoaded('instagram') || isLoaded('facebook')).toBe(true).then(() => loadMedia(mediaEndpoint))
}
</script>

<style>
.share-modal .modal__close {
  @apply right-0 top-0 -mr-3 -mt-3 h-6 w-6 rounded-full bg-white shadow hover:text-slate-900;
}
</style>
